"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { PositionAssetTypeEnum } from "@/src/features/api/memberApi";
import { QUERY_KEYS } from "@/src/config/constant";

// 定义分页参数类型
type PositionOrderParams = [string, any];

export const usePositionOrders = (
  accountId: string,
  query?: {
    /** Asset ID */
    asset_id?: string;
    /** Asset type enumeration */
    asset_type?: PositionAssetTypeEnum;
  },
) => {
  const { memberApi } = useOspApiClientProvider();

  const initialPageParam: PositionOrderParams = [
    accountId,
    {
      limit: 20,
      nextToken: undefined,
      ...query,
    },
  ] as PositionOrderParams;

  const infiniteQuery = useInfiniteQuery({
    queryKey: [QUERY_KEYS.POSITION_ORDERS, accountId, query],
    initialPageParam,
    queryFn: async ({ pageParam }) => {
      if (!memberApi?.positions?.getPositionOrders) {
        throw new Error("Position Orders API not available");
      }

      // 使用类型断言确保 pageParam 是正确的类型
      const params = pageParam as PositionOrderParams;
      const [accountId, queryWithToken] = params;

      const response = await memberApi.positions.getPositionOrders(accountId, {
        ...queryWithToken,
        nextToken: queryWithToken?.nextToken
          ? decodeURIComponent(queryWithToken.nextToken)
          : undefined,
      });

      return response.data;
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // 如果返回的响应中包含 next_token，则使用它获取下一页
      if (lastPage?.data?.next_token) {
        // 复制上一次的参数并更新 next_token
        const newParams = [...lastPageParam] as PositionOrderParams;
        if (newParams[1]) {
          newParams[1] = {
            ...newParams[1],
            nextToken: lastPage.data.next_token,
          };
        }
        return newParams;
      }
      // 没有更多数据时返回 undefined
      return undefined;
    },
    enabled: !!accountId && !!memberApi,
    select: (data) => {
      return {
        pages: data.pages,
        pageParams: data.pageParams,
        // Flatten all orders from all pages
        orders: data.pages?.map((page) => page?.data?.rows || []).flat() || [],
        // Get the next token from the last page
        nextToken: data.pages[data.pages.length - 1]?.data?.next_token,
        // Total count from the first page
        total: data.pages[0]?.data?.total || 0,
      };
    },
  });

  return infiniteQuery;
};
